/**
 * @file     SellerLeadFlow.tsx
 * @purpose  The one seller conversion form for every seller page, the Fast Track guide and /l seller landings: renders a seller FlowConfig (valuation by default, its Fast Track and landing variants, or the off-market flow) through the shared FlowRenderer, prefills the home's address, type, beds and baths from the sellerPrefill query params, stamps hub provenance into tags and landing attribution onto the lead, and links the valuation form to the off-market flow.
 */
'use client';

import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { FlowRenderer } from '@/features/lead-flows/FlowRenderer';
import { SELLER_FLOW_CONFIG } from '@/features/lead-flows/configs/sellerFlow';
import type { FlowConfig } from '@/features/lead-flows/types';
import type { FlowPresentationMode } from '@/features/lead-flows/components/FlowFrame';
import { SellOffMarketLink } from '@/features/seo/SellOffMarketLink';
import { sellerPrefillAnswers } from '@/features/lead-flows/sellerPrefill';

type SellerLeadFlowProps = {
  sourcePage: string;
  /** Seller flow to render. Defaults to the valuation flow. */
  config?: FlowConfig;
  /** Answers carried in from the page, e.g. the area a hub link preselected. */
  initialAnswers?: Record<string, string>;
  neighbourhood?: string;
  region?: string;
  building?: string;
  city?: string;
  propertyType?: string;
  /** /l landing attribution, stamped on the lead and on the Ads and Meta conversion. */
  landing?: FlowConfig['landing'];
  /** FlowRenderer frame; 'aside' fits a hero aside. */
  presentation?: FlowPresentationMode;
};

export function SellerLeadFlow({
  sourcePage,
  config: baseConfig = SELLER_FLOW_CONFIG,
  initialAnswers,
  neighbourhood,
  region,
  building,
  city,
  propertyType,
  landing,
  presentation,
}: SellerLeadFlowProps) {
  // "Value my home" on a listing carries that home's facts in the query, so
  // the flow opens on the first step it still needs. Page answers win.
  const searchParams = useSearchParams();
  const answers = useMemo(() => {
    const prefill = sellerPrefillAnswers(searchParams);
    return Object.keys(prefill).length > 0 ? { ...prefill, ...initialAnswers } : initialAnswers;
  }, [initialAnswers, searchParams]);
  const config = useMemo(() => {
    const provenance = [
      ...(building ? [`Building: ${building}`] : []),
      ...(neighbourhood ? [`Neighbourhood: ${neighbourhood}`] : []),
      ...(region ? [`Region: ${region}`] : []),
      ...(city ? [`City: ${city}`] : []),
      ...(propertyType ? [`Property type: ${propertyType}`] : []),
    ];
    const withLanding = landing ? { ...baseConfig, landing } : baseConfig;
    if (provenance.length === 0) return withLanding;
    return {
      ...withLanding,
      buildTags: (answers: Record<string, string>) => [
        ...(baseConfig.buildTags?.(answers) ?? []),
        ...provenance,
      ],
    };
  }, [baseConfig, building, city, landing, neighbourhood, propertyType, region]);

  return (
    <>
      <FlowRenderer
        config={config}
        sourcePage={sourcePage}
        initialAnswers={answers}
        startAtFirstQuestion
        presentation={presentation}
      />
      {baseConfig.flowId === SELLER_FLOW_CONFIG.flowId ? (
        <SellOffMarketLink sourcePage={sourcePage} city={city} />
      ) : null}
    </>
  );
}
